import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { showNotification } from '../../LanghoutBetonPortal';
import { DropdownMultiselectElement } from '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';

import {
  convertReadableToStockCountStatus,
  convertStockCountStatusToReadable,
  formatDate,
  formatTimeOnly,
} from '../../util/util';
import { repeat } from 'lit/directives/repeat.js';
import { StockCount } from '../../types/types';
import '../../elements/list-nagivation/list-navigation';
import { messageIcon, refreshIcon, sortIcon } from '../../elements/icons/icons';
import {
  addHistory,
  getQueryParamURL,
  getSearchParam,
} from '../../util/list_util';
import { sharedStyles } from '../../elements/shared-styles';
@customElement('langhout-beton-list-stock-count')
export class LanghoutBetonListStockCount extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .row {
        display: flex;
        flex-direction: row;
      }

      .row-flex {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .filter-row {
        min-height: 130px;
      }

      .filter-column {
        margin-bottom: 20px;
      }

      .fat {
        font-weight: 900;
        color: var(--menu-color);
      }

      .label {
        font-weight: bold;
      }

      @media (max-width: 701px) {
        .label {
          text-align: left;
        }
      }

      .product-column {
        text-align: left;
        width: 100%;
        min-width: 120px;
        min-height: calc(54px - 24px);
      }

      .account-id-click {
        font-weight: bold;
        text-decoration: underline;
        text-decoration-color: var(--primary-color);
      }

      .account-id-click:hover {
        cursor: pointer;
      }

      .border-bot {
        border-bottom: 2px solid var(--menu-color);
      }

      .column-large {
        text-align: left;
        min-width: calc(350px - 20px);
        max-width: calc(350px - 20px);
      }

      .table-wrapper {
        overflow-x: auto;
      }

      .table-header {
        color: var(--text-color-light);
      }

      .header-column {
        background-color: var(--menu-color);
        padding-left: 12px;
      }

      .column-border-right {
        border-right: 2px solid var(--menu-color);
      }

      .navigation-wrapper {
        margin-top: 20px;
      }

      langhout-beton-view-account {
        position: absolute;
        top: 84px;
        right: -200vw;
      }

      :host([backAnimation]) langhout-beton-view-account {
        transition-duration: 500ms;
      }

      :host([viewAccount]) langhout-beton-view-account {
        transition-duration: 500ms;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        width: calc(100vw - 60px);
        height: calc(100vh -84px);
        top: 84px;
      }

      .sort-icon-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .sort-icon {
        transform: rotate(90deg);
      }

      .sort-icon:hover {
        cursor: pointer;
      }

      .sort-active {
        color: var(--text-color-main);
      }

      .account-id-column {
        padding-left: 10px;
        padding-right: 10px;
      }

      #resetButton,
      #searchExactButton {
        margin-top: 20px;
        margin-left: 30px;
      }
    `,
  ];

  private requestCounter = 0;

  private listTimer = 0;

  @property({ type: Array })
  private stockCounts: StockCount[] = [];

  @property({ type: Number })
  private accountCount = 0;

  @query('#dropdownElement')
  public dropdownElement?: DropdownMultiselectElement;

  @query('#code')
  public code?: HTMLInputElement;

  @query('#searchCode')
  public searchCode?: HTMLInputElement;

  private offset = 0;

  private limit = 250;

  @property({ type: Boolean, reflect: true })
  public viewAccount = false;

  @property({ type: Boolean, reflect: true })
  public backAnimation = false;

  @query('#fromDate')
  public fromDate?: HTMLInputElement;

  @query('#untilDate')
  public untilDate?: HTMLInputElement;

  @query('#withoutAutomaticStockCount')
  public withoutAutomaticStockCount?: LanghoutCheckbox;

  private orderByColumn = '';

  private orderByDirection = '';

  constructor() {
    super();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
  }

  protected routerLocationChanged = (): void => {
    this.setPropertiesFromURL();
    this.listStockCounts(false);
  };

  protected firstUpdated(): void {
    window.addEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    this.setPropertiesFromURL();
    this.listStockCounts(false);
  }

  protected setPropertiesFromURL = (): void => {
    const url = getQueryParamURL();
    if (this.dropdownElement) {
      const stockStatuses = getSearchParam(url, 'stock_count_statuses');
      if (stockStatuses !== '') {
        const res: string[] = [];
        const statuses = stockStatuses.split(',');
        statuses.forEach(e => {
          const status = convertStockCountStatusToReadable(e);
          if (!res.includes(status)) {
            res.push(status);
          }
        });
        this.dropdownElement.selected = res;
      } else {
        this.dropdownElement.selected = ['Aangemaakt'];
      }
    }
    if (this.code) {
      this.code.value = getSearchParam(url, 'product_code');
    }

    if (this.fromDate) {
      const splittedDate = getSearchParam(url, 'from').split('-');
      if (splittedDate.length === 3) {
        this.fromDate.value = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
      } else {
        this.fromDate.value = '';
      }
    }

    if (this.untilDate) {
      const splittedDate = getSearchParam(url, 'until').split('-');
      if (splittedDate.length === 3) {
        this.untilDate.value = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
      } else {
        this.untilDate.value = '';
      }
    }

    if (this.withoutAutomaticStockCount) {
      this.withoutAutomaticStockCount.checked =
        getSearchParam(url, 'without_automatic_stock_count') === 'true';
    }

    const orderBy = getSearchParam(url, 'order_by');
    if (orderBy !== '') {
      this.orderByColumn = orderBy;
    }
    const orderDirection = getSearchParam(url, 'order_direction');
    if (orderDirection !== '') {
      this.orderByDirection = orderDirection;
    }
  };

  render() {
    return html`
      ${this.renderFilterRow()}
      <div class="table-wrapper">
        <div class="row table-header">
          <div class="column header-column product-column">
            <div class="row">
              <h3>Machine</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive(
                  'production_unit_id',
                )}"
                @click="${() => {
                  this.orderColumns('production_unit_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Product</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('product_code')}"
                @click="${() => {
                  this.orderColumns('product_code');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Telling (Stuks)</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('count')}"
                @click="${() => {
                  this.orderColumns('count');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Automatische telling</h3>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Door</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive(
                  'created_user_id',
                )}"
                @click="${() => {
                  this.orderColumns('created_user_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Status</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('status')}"
                @click="${() => {
                  this.orderColumns('status');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Datum</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('time')}"
                @click="${() => {
                  this.orderColumns('time');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
        </div>
        ${this.renderRows()}
      </div>
      <div class="navigation-wrapper">
        <list-navigation
          index="${this.offset}"
          limit="${this.limit}"
          total="${this.accountCount}"
          @change="${(e: CustomEvent) => {
            if (this.offset != e.detail.value) {
              this.offset = parseInt(e.detail.value);
              this.listWithBackoffWithoutReset();
            }
          }}"
        ></list-navigation>
      </div>
    `;
  }

  renderFilterRow = () => {
    return html`
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Status</div>
          <div class="input-field">
            <dropdown-multiselect-element
              id="dropdownElement"
              .selectable="${['Aangemaakt', 'Wordt verwerkt', 'Verwerkt']}"
              @change="${() => {
                this.listStockCounts();
              }}"
            ></dropdown-multiselect-element>
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Code</div>
          <div class="input-field">
            <input
              type="text"
              id="code"
              name="code"
              @input="${() => {
                this.listStockCounts();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Van</div>
          <div class="input-field">
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              @change="${() => {
                this.listStockCounts();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Tot</div>
          <div class="input-field">
            <input
              type="date"
              id="untilDate"
              name="untilDate"
              @change="${() => {
                this.listStockCounts();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Zonder automatische telling</div>
          <div class="input-field">
            <langhout-checkbox
              id="withoutAutomaticStockCount"
              @click="${() => {
                this.listStockCounts();
              }}"
            ></langhout-checkbox>
          </div>
        </div>

        <langhout-button
          text="Reset filters"
          id="resetButton"
          @click="${(): void => {
            window.location.href = '/stock-count';
          }}"
        ></langhout-button>

        <div class="fill"></div>
        <div class="row filter-column"></div>
      </div>

      <div class="row-flex filter-row">
        <div class="fill"></div>
        <div
          @click="${() => {
            showNotification('Refreshing stockCounts');
            this.listWithBackoff();
          }}"
        >
          ${refreshIcon()}
        </div>
      </div>
    `;
  };

  renderRows = () => {
    return html` ${repeat(
      this.stockCounts,
      stockCount => stockCount.id,
      (stockCount, index) => {
        let tableColumnClass = 'border-bot';
        if (index == this.stockCounts.length - 1) {
          tableColumnClass = '';
        }
        return html`
          <div
            class="row table-row"
            @click="${() => {
              window.location.href = `/view-stock-count?id=${stockCount.id}`;
            }}"
          >
            <div
              class="row account-id-column table-column account-id-click column-border-right product-column ${tableColumnClass}"
            >
              ${stockCount.production_unit_name}
              <div class="fill"></div>
              <div>${this.renderMessageIcon(stockCount)}</div>
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${stockCount.product_name}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${stockCount.count}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${
                stockCount.automatic_stock_count
                  ? html`Ja ${formatDate(stockCount.automatic_stock_count.time)}
                    ${formatTimeOnly(stockCount.automatic_stock_count.time)}`
                  : 'Nee'
              }
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${stockCount.created_user}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${convertStockCountStatusToReadable(stockCount.status)}
            </div>

            <div class="column table-column product-column ${tableColumnClass}">
              ${formatDate(stockCount.time)} ${formatTimeOnly(stockCount.time)}
            </div>
          </div>
        `;
      },
    )}`;
  };

  formatCustomerDescriptionType = (descType: string): string => {
    if (descType === 'Supplier') {
      return 'Leverancier';
    }
    return 'Klant';
  };

  isSortActive = (column: string): string => {
    if (this.orderByColumn === column) {
      return 'sort-active';
    }
    return '';
  };

  renderMessageIcon = (stockCount: StockCount) => {
    if (!stockCount.comments || stockCount.comments.length === 0) {
      return html``;
    }
    return html` <div class="message-icon-wrapper">${messageIcon()}</div> `;
  };

  orderColumns = (column: string) => {
    if (this.orderByColumn === '' && this.orderByDirection === '') {
      this.orderByDirection = 'asc';
      this.orderByColumn = column;
    } else if (
      this.orderByColumn === column &&
      this.orderByDirection === 'asc'
    ) {
      this.orderByDirection = 'desc';
      this.orderByColumn = column;
    } else if (
      this.orderByColumn === column &&
      this.orderByDirection === 'desc'
    ) {
      this.orderByDirection = '';
      this.orderByColumn = '';
    } else {
      this.orderByDirection = 'asc';
      this.orderByColumn = column;
    }

    this.listStockCounts();
  };

  listWithBackoffWithoutReset = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.listStockCounts();
    }, 50);
  };

  listWithBackoff = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.offset = 0;
      this.listStockCounts();
    }, 50);
  };

  createFilterArray = (): string[] => {
    const filterArray: string[] = [];
    const statusFilterIntegerArray: Array<string> = [];
    if (this.dropdownElement) {
      this.dropdownElement.selected.forEach(element => {
        statusFilterIntegerArray.push(
          convertReadableToStockCountStatus(element),
        );
      });
    }
    if (statusFilterIntegerArray.length > 0) {
      filterArray.push(
        `stock_count_statuses=${statusFilterIntegerArray.join(',')}`,
      );
    }

    const codeFilter = this.code ? this.code.value : '';
    if (codeFilter != '') {
      filterArray.push(`product_code=${codeFilter}`);
    }

    const fromDate = this.fromDate ? this.fromDate.value : '';
    if (fromDate != '') {
      filterArray.push(`from=${formatDate(fromDate)}`);
    }

    const untilDate = this.untilDate ? this.untilDate.value : '';
    if (untilDate != '') {
      filterArray.push(`until=${formatDate(untilDate)}`);
    }

    if (this.withoutAutomaticStockCount?.checked) {
      filterArray.push('without_automatic_stock_count=true');
    }

    filterArray.push(`order_by=${this.orderByColumn}`);
    filterArray.push(`order_direction=${this.orderByDirection}`);
    return filterArray;
  };

  listStockCounts = async (recordHistory: boolean = true) => {
    this.requestCounter++;
    const requestCounter = this.requestCounter;

    const filterArray = this.createFilterArray();

    let path = `/stock/count?limit=${this.limit}&offset=${this.offset}`;
    const filter = filterArray.join('&');
    if (filter !== '') {
      if (recordHistory) {
        addHistory(filter);
      }
      path += '&' + filter;
    }

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (!isSuccessfulAPIResponse(result)) {
      return;
    }
    if (requestCounter != this.requestCounter) {
      return;
    }
    const xCount = result.headers.get('x-count');
    if (xCount) {
      this.accountCount = parseInt(xCount);
    }

    this.stockCounts = result.body;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-list-stock-count': LanghoutBetonListStockCount;
  }
}
